/**
 * UTMパラメータ関連のユーティリティ関数
 * 広告流入判定とUTMパラメータ管理を一元化
 */

/**
 * UTMソースの定数
 */
export const UTM_SOURCE = {
  META: 'meta',
  TIKTOK: 'tiktok',
} as const;

/**
 * UTMメディアの定数
 */
export const UTM_MEDIUM = {
  AD: 'paid_social',
  ORGANIC: 'organic',
} as const;

/**
 * UTMソースの型
 */
export type UtmSource = typeof UTM_SOURCE[keyof typeof UTM_SOURCE];

/**
 * UTMメディアの型
 */
export type UtmMedium = typeof UTM_MEDIUM[keyof typeof UTM_MEDIUM];

/**
 * UTMパラメータの型
 */
export interface UtmParams {
  source?: string | null;
  medium?: string | null;
  campaign?: string | null;
  content?: string | null;
  term?: string | null;
}

/**
 * UTMパラメータのキャッシュ（メモ化用）
 */
let cachedUtmParams: UtmParams | null = null;

/**
 * UTMパラメータを取得（メモ化対応）
 * @returns UTMパラメータオブジェクト
 */
export const getUtmParams = (): UtmParams => {
  if (cachedUtmParams) return cachedUtmParams;
  
  if (typeof window === 'undefined') {
    return {};
  }
  
  const params = new URLSearchParams(window.location.search);
  cachedUtmParams = {
    source: params.get('utm_source'),
    medium: params.get('utm_medium'),
    campaign: params.get('utm_campaign'),
    content: params.get('utm_content'),
    term: params.get('utm_term'),
  };
  
  return cachedUtmParams;
};

/**
 * UTMパラメータのキャッシュをクリア
 */
export const clearUtmCache = (): void => {
  cachedUtmParams = null;
};

/**
 * UTMパラメータをsessionStorageに保存
 */
export const saveUtmParams = (): void => {
  if (typeof window === 'undefined') return;
  
  const utmParams = getUtmParams();
  // 有効なUTMパラメータがある場合のみ保存
  if (utmParams.source || utmParams.medium) {
    try {
      sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    } catch (e) {
      // sessionStorageが使えない場合（プライベートモードなど）は無視
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to save UTM params:', e);
      }
    }
  }
};

/**
 * sessionStorageからUTMパラメータを復元
 * @returns 保存されたUTMパラメータ
 */
export const getStoredUtmParams = (): UtmParams => {
  if (typeof window === 'undefined') return {};
  
  try {
    const stored = sessionStorage.getItem('utm_params');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    // 無視
  }
  
  return {};
};

/**
 * 優先順位付きでUTMパラメータを取得
 * URLパラメータ > sessionStorage
 * @returns 統合されたUTMパラメータ
 */
export const getMergedUtmParams = (): UtmParams => {
  const urlParams = getUtmParams();
  const storedParams = getStoredUtmParams();
  
  return {
    source: urlParams.source || storedParams.source,
    medium: urlParams.medium || storedParams.medium,
    campaign: urlParams.campaign || storedParams.campaign,
    content: urlParams.content || storedParams.content,
    term: urlParams.term || storedParams.term,
  };
};

/**
 * UTMパラメータを含む完全なURLを構築
 * @param pathname パス名（デフォルト: 現在のパス）
 * @returns UTMパラメータを含む完全なURL
 */
export const buildUrlWithUtm = (pathname?: string): string => {
  if (typeof window === 'undefined') {
    return '';
  }
  
  const utmParams = getMergedUtmParams();
  const baseUrl = window.location.origin;
  const path = pathname || window.location.pathname;
  
  // UTMパラメータをクエリ文字列として構築
  const queryParams = new URLSearchParams();
  if (utmParams.source) queryParams.set('utm_source', utmParams.source);
  if (utmParams.medium) queryParams.set('utm_medium', utmParams.medium);
  if (utmParams.campaign) queryParams.set('utm_campaign', utmParams.campaign);
  if (utmParams.content) queryParams.set('utm_content', utmParams.content);
  if (utmParams.term) queryParams.set('utm_term', utmParams.term);
  
  const queryString = queryParams.toString();
  return queryString ? `${baseUrl}${path}?${queryString}` : `${baseUrl}${path}`;
};





